import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Badge,
  Skeleton,
  Alert,
  alpha,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocalHospital as DoctorsIcon,
  CheckCircle as ActiveIcon,
  Warning as BusyIcon,
  Schedule as PendingIcon,
  EventBusy as LeaveIcon,
  Person as PersonIcon,
  TrendingUp as StatsIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  MedicalServices as MedicalIcon,
  Star as StarIcon,
  Block as BlockIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface DoctorListItem {
  id: number;
  employeeId: string;
  fullName: string;
  email: string;
  mobile: string;
  specialization: string;
  qualification: string;
  department: string;
  designation: string;
  status: string;
  statusColor: string;
  experienceYears: number;
  profilePhotoUrl: string;
  currentStatus: string;
  currentStatusColor: string;
  todayAppointments: number;
  completedToday: number;
  pendingCount: number;
  avgRating: number;
  totalPatients: number;
  shiftTiming: string;
  onLeaveToday: boolean;
  createdAt: string;
}

interface DoctorStatistics {
  totalDoctors: number;
  activeDoctors: number;
  availableNow: number;
  busyNow: number;
  onVisitNow: number;
  onLeaveToday: number;
  offlineNow: number;
  pendingApprovals: number;
  newThisMonth: number;
  totalAppointmentsToday: number;
  completedToday: number;
  pendingToday: number;
  specializationDistribution: { specialization: string; count: number }[];
  departmentDistribution: { department: string; count: number }[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

/**
 * Doctor Management Page - Feature 3
 * Healthcare Operations Command Center
 */
const DoctorManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [statistics, setStatistics] = useState<DoctorStatistics | null>(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentStatusFilter, setCurrentStatusFilter] = useState('');
  const [specializationFilter, setSpecializationFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorListItem | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN'].includes(user?.role || '')) {
      navigate('/login');
      return;
    }
    fetchDoctors();
    fetchStatistics();
  }, [isAuthenticated, user, navigate, page, rowsPerPage, statusFilter, currentStatusFilter]);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', rowsPerPage.toString());
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (currentStatusFilter) params.append('currentStatus', currentStatusFilter);
      if (specializationFilter) params.append('specialization', specializationFilter);

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/admin/doctors?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDoctors(response.data.data.content);
        setTotalElements(response.data.data.totalElements);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/admin/doctors/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (err) {
      // Ignore stats errors
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchDoctors();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, doctor: DoctorListItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedDoctor(doctor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoctor(null);
  };

  const handleViewDoctor = () => {
    if (selectedDoctor) navigate(`/doctors/${selectedDoctor.id}`);
    handleMenuClose();
  };

  const handleUpdateStatus = async () => {
    if (!selectedDoctor || !newStatus) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/admin/doctors/${selectedDoctor.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDoctors();
      fetchStatistics();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    }

    setStatusDialogOpen(false);
    handleMenuClose();
  };

  const getCurrentStatusBadge = (status: string) => {
    const config: Record<string, { color: 'success' | 'warning' | 'error' | 'info' | 'default'; icon: React.ReactElement }> = {
      AVAILABLE: { color: 'success', icon: <ActiveIcon sx={{ fontSize: 14 }} /> },
      BUSY: { color: 'warning', icon: <BusyIcon sx={{ fontSize: 14 }} /> },
      ON_VISIT: { color: 'info', icon: <MedicalIcon sx={{ fontSize: 14 }} /> },
      LEAVE: { color: 'error', icon: <LeaveIcon sx={{ fontSize: 14 }} /> },
      OFFLINE: { color: 'default', icon: <PersonIcon sx={{ fontSize: 14 }} /> },
    };
    const c = config[status] || config.OFFLINE;
    return <Chip icon={c.icon} label={status} size="small" color={c.color} sx={{ fontWeight: 600 }} />;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '--/--/----';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading && doctors.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Skeleton variant="rectangular" height={100} sx={{ mt: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2, borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Doctor Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage all registered doctors
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { fetchDoctors(); fetchStatistics(); }}>
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<ExportIcon />}>
            Export
          </Button>
        </Box>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2563eb' }}>{statistics.totalDoctors}</Typography>
                <Typography variant="caption" color="text.secondary">Total Doctors</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>{statistics.availableNow}</Typography>
                <Typography variant="caption" color="text.secondary">Available Now</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>{statistics.busyNow}</Typography>
                <Typography variant="caption" color="text.secondary">Busy</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2563eb' }}>{statistics.onVisitNow}</Typography>
                <Typography variant="caption" color="text.secondary">On Visit</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>{statistics.onLeaveToday}</Typography>
                <Typography variant="caption" color="text.secondary">On Leave</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7c3aed' }}>{statistics.pendingApprovals}</Typography>
                <Typography variant="caption" color="text.secondary">Pending Approval</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content */}
      <Paper sx={{ borderRadius: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid #e5e7eb', p: 2 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="All Doctors" />
            <Tab label="Available" />
            <Tab label="On Leave" />
            <Tab label="Pending Approval" />
          </Tabs>
        </Box>

        {/* Filters */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search doctors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={currentStatusFilter}
              label="Status"
              onChange={(e) => setCurrentStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="AVAILABLE">Available</MenuItem>
              <MenuItem value="BUSY">Busy</MenuItem>
              <MenuItem value="ON_VISIT">On Visit</MenuItem>
              <MenuItem value="LEAVE">On Leave</MenuItem>
              <MenuItem value="OFFLINE">Offline</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<FilterIcon />} onClick={handleSearch}>
            Apply Filters
          </Button>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                <TableCell>Doctor</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Current Status</TableCell>
                <TableCell>Today's Stats</TableCell>
                <TableCell>Performance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {doctors.map((doctor) => (
                <TableRow key={doctor.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={doctor.profilePhotoUrl}
                        sx={{ bgcolor: 'transparent' , width: 40, height: 40 }}
                      >
                        {doctor.fullName?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{doctor.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">{doctor.employeeId}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{doctor.specialization}</Typography>
                    <Typography variant="caption" color="text.secondary">{doctor.qualification}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={doctor.department || 'Unassigned'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {getCurrentStatusBadge(doctor.currentStatus)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Total Today">
                        <Chip label={`${doctor.todayAppointments} Total`} size="small" />
                      </Tooltip>
                      <Tooltip title="Completed">
                        <Chip label={`${doctor.completedToday} Done`} size="small" color="success" />
                      </Tooltip>
                      <Tooltip title="Pending">
                        <Badge badgeContent={doctor.pendingCount} color="warning">
                          <PendingIcon color="action" />
                        </Badge>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StarIcon sx={{ color: '#f59e0b', fontSize: 16 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {doctor.avgRating?.toFixed(1) || '4.5'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({doctor.totalPatients || 0} patients)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, doctor)}>
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {doctors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No doctors found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 20, 50]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDoctor}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (selectedDoctor) navigate(`/doctors/${selectedDoctor.id}/schedule`); handleMenuClose(); }}>
          <ListItemIcon><StatsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Schedule</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setNewStatus('AVAILABLE'); setStatusDialogOpen(true); }}>
          <ListItemIcon><ActiveIcon fontSize="small" sx={{ color: '#10b981' }} /></ListItemIcon>
          <ListItemText>Set Available</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setNewStatus('BUSY'); setStatusDialogOpen(true); }}>
          <ListItemIcon><BusyIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon>
          <ListItemText>Set Busy</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setNewStatus('LEAVE'); setStatusDialogOpen(true); }}>
          <ListItemIcon><LeaveIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText>Mark on Leave</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: '#ef4444' }}>
          <ListItemIcon><BlockIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText>Deactivate</ListItemText>
        </MenuItem>
      </Menu>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Update Doctor Status</DialogTitle>
        <DialogContent>
          <Typography>
            Change status for <strong>{selectedDoctor?.fullName}</strong> to <strong>{newStatus}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateStatus}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorManagementPage;